import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LanguagePicker from './LanguagePicker';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-[48px] left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-[#84bc00]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - Icon always shown, text only on xl screens (1280px+) */}
          <Link to="/" className="flex items-center space-x-2 xl:space-x-3 group flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_29b477db-9dec-46a2-805c-b00d50eeabf6/artifacts/5jpkt2g1_customcolor_icon_customcolor_background.png" 
              alt="Exciting.Travel" 
              className="h-10 md:h-12 w-auto"
            />
            <span className="hidden xl:inline text-2xl font-black tracking-tight group-hover:opacity-90 transition-opacity">
              <span className="text-[#ffb84d]">EXCITING</span><span className="text-white">.TRAVEL</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3 lg:space-x-6 xl:space-x-8">
            <Link
              to="/trips"
              className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                isActive('/trips') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              TRIPS
            </Link>
            <Link
              to="/destinations"
              className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                isActive('/destinations') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.destinations').toUpperCase()}
            </Link>
            <Link
              to="/about"
              className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                isActive('/about') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.about').toUpperCase()}
            </Link>
            <Link
              to="/calendar"
              className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                isActive('/calendar') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {language === 'es' ? 'CALENDARIO' : 'CALENDAR'}
            </Link>
            <Link
              to="/members"
              className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                isActive('/members') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.members').toUpperCase()}
            </Link>
            
            {/* Admin link - only show if logged in AND is admin */}
            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                className={`text-sm font-bold tracking-wide transition-colors duration-300 ${
                  isActive('/admin') ? 'text-[#ffb84d]' : 'text-[#ffb84d] hover:text-white'
                }`}
              >
                ADMIN
              </Link>
            )}
            
            {/* Language Picker */}
            <LanguagePicker />
            
            {/* Auth buttons */}
            {user ? (
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-black font-bold"
              >
                {t('nav.logout').toUpperCase()}
              </Button>
            ) : (
              <>
                <Link to="/trips">
                  <Button className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#84bc00]/50">
                    {t('nav.exploreTrips').toUpperCase()}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <LanguagePicker />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-[#84bc00] transition-colors duration-300"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-gray-900 border-t border-[#84bc00]/20">
          <div className="px-4 py-6 space-y-4">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.home').toUpperCase()}
            </Link>
            <Link
              to="/trips"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/trips') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              TRIPS
            </Link>
            <Link
              to="/destinations"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/destinations') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.destinations').toUpperCase()}
            </Link>
            <Link
              to="/about"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/about') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.about').toUpperCase()}
            </Link>
            <Link
              to="/calendar"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/calendar') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {language === 'es' ? 'CALENDARIO' : 'CALENDAR'}
            </Link>
            <Link
              to="/members"
              onClick={() => setIsOpen(false)}
              className={`block text-lg font-bold tracking-wide transition-colors duration-300 ${
                isActive('/members') ? 'text-[#84bc00]' : 'text-white hover:text-[#84bc00]'
              }`}
            >
              {t('nav.members').toUpperCase()}
            </Link>
            
            {/* Admin link mobile - only show if logged in AND is admin */}
            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setIsOpen(false)}
                className="block text-lg font-bold tracking-wide text-[#ffb84d] hover:text-white transition-colors duration-300"
              >
                ADMIN
              </Link>
            )}
            
            {user ? (
              <Button 
                onClick={handleLogout}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold"
              >
                {t('nav.logout').toUpperCase()}
              </Button>
            ) : (
              <>
                <Link to="/trips" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold tracking-wide mb-2">
                    {t('nav.exploreTrips').toUpperCase()}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
